import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import host from "../../../../global.jsx";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useMediaPipe } from "../../../../contexts/MediaPipeContext.jsx";
import useMultiMonitor from "../../useMultiMonitor.jsx";
import { MODEL_URLS } from "../constants.js";
import { useScreenProctoring } from "../../../../components/Instructions/hooks/useScreenProctoring.js";
import { candidateAuthHeader } from "../../../../utils/candidateAuth.js";

const ProctoringSystem = ({
  isEnabled = false,
  externalVideoRef = null,
  interviewId,
  onViolation = () => {},
  onCameraReady = () => {},
  className = "",
  isInterviewActive = false,
  onStickyAlert = () => {},
  onFullscreenExitViolation = () => {},
  onTabSwitchViolation = () => {},
  onFaceViolation = () => {},
  onMultipleFaceViolation = () => {},
  onProhibitedObjectViolation = () => {},
  onBodyPostureViolation = () => {},
  onGazeViolation = () => {},
  onFullscreenChange = () => {},
}) => {
  const location = useLocation();
  const {
    getDetectors,
    isInitialized: isMediaPipeReady,
    configs,
  } = useMediaPipe();
  const localVideoRef = useRef(null);
  const videoRef = externalVideoRef || localVideoRef;
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastDetectionTime = useRef(0);
  const lastLoggedMap = useRef({});
  const eyeClosedSince = useRef(null);
  const headTurnSince = useRef(null);
  const activeToastsRef = useRef({});
  const fullscreenToastId = useRef(null);
  const gazeHistoryX = useRef([]);
  const gazeHistoryY = useRef([]);
  const eyeAspectRatioHistory = useRef([]);
  const consecutiveLookAwayFrames = useRef(0);
  const lastValidGazeDirection = useRef({ x: 0, y: 0 });
  const noFaceSinceRef = useRef(null);
  const bodyMissingSinceRef = useRef(null);
  const gazeDirectionHistory = useRef([]); 
  const gazeWindow = useRef([]); 
  const dominantDirectionHistory = useRef([]); 
  const lastGazeViolationTime = useRef(0);
  const gazeAwayStartTime = useRef(null);
  const lastGazeDirection = useRef(null);

  const [faceDetector, setFaceDetector] = useState(null);
  const [handLandmarker, setHandLandmarker] = useState(null);
  const [faceLandmarker, setFaceLandmarker] = useState(null);
  const [objectDetector, setObjectDetector] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [activeViolationToasts, setActiveViolationToasts] = useState({});
  const [proctoringAlerts, setProctoringAlerts] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(
    !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    )
  );

  const isMultiMonitor = useMultiMonitor();
  const consecutiveMultiFaceFrames = useRef(0);
  const lastMultiFaceTime = useRef(null);
  const faceTracker = useRef({
    primaryFace: null,
    faceHistory: [],
    maxHistorySize: 10,
  });

  // Screen Shot Vars
  const {captureScreenshotBlob} = useScreenProctoring({})

  const calculateFaceArea = (boundingBox) => {
    if (!boundingBox) return 0;
    return boundingBox.width * boundingBox.height;
  };

  const calculateFaceCenter = (boundingBox) => {
    if (!boundingBox) return { x: 0, y: 0 };
    return {
      x: boundingBox.originX + boundingBox.width / 2,
      y: boundingBox.originY + boundingBox.height / 2,
    };
  };

  const isValidFaceDetection = (detection) => {
    if (!detection.boundingBox) return false;

    const area = calculateFaceArea(detection.boundingBox);
    const confidence = detection.categories[0]?.score || 0;
    const width = detection.boundingBox.width;
    const height = detection.boundingBox.height;

    const videoArea =
      (videoRef.current?.videoWidth || 640) *
      (videoRef.current?.videoHeight || 480);
    const relativeArea = area / videoArea;

    // More flexible size rules to catch distant faces:
    // - Minimum size: 0.2% of video area OR 25x25 pixels (for distant faces)
    // - Maximum size: 50% of video area (someone very close)
    // - Aspect ratio check: faces should be roughly rectangular (not too wide/tall)
    const aspectRatio = width / height;
    const isReasonableAspectRatio = aspectRatio > 0.5 && aspectRatio < 2.0; // Not too wide or tall

    let requiredConfidence;
    if (relativeArea > 0.05) {
      // Large faces (close to camera) - normal confidence
      requiredConfidence = 0.6;
    } else if (relativeArea > 0.01) {
      // Medium faces - slightly higher confidence
      requiredConfidence = 0.7;
    } else {
      // Small/distant faces - higher confidence required
      requiredConfidence = 0.8;
    }

    return (
      relativeArea > 0.002 && // 0.2% minimum (allows distant faces)
      relativeArea < 0.5 && // 50% maximum
      confidence > requiredConfidence &&
      width > 20 && // Minimum 20 pixels wide (reduced from 50)
      height > 25 && // Minimum 25 pixels tall (reduced from 50)
      isReasonableAspectRatio && // Face-like proportions
      // Additional check: filter out very thin/wide detections that are likely objects
      !(width < 30 && height > width * 2) && // Not a thin vertical object
      !(height < 30 && width > height * 2) // Not a thin horizontal object
    );
  };

  const isProperBodyPosture = (faceLandmarks, videoWidth, videoHeight) => {
    if (!faceLandmarks || faceLandmarks.length < 468) return false;

    try {
      // Key facial landmarks
      const chin = faceLandmarks[152]; // Bottom of face
      const forehead = faceLandmarks[10]; // Top of face
      const noseTip = faceLandmarks[1]; // Nose tip (face center reference)
      
      // Calculate face dimensions in pixel coordinates
      const faceTop = forehead.y * videoHeight;
      const faceBottom = chin.y * videoHeight;
      const faceCenterY = noseTip.y * videoHeight;
      
      const faceHeight = faceBottom - faceTop;
      
      // Calculate how much space is available below the chin
      const spaceBelow = videoHeight - faceBottom;
      const spaceBelowRatio = spaceBelow / faceHeight;
      
      // Calculate face position in frame
      const faceHeightRatio = faceHeight / videoHeight;
      const faceCenterRatio = faceCenterY / videoHeight;
      
      // RELAXED Proper posture conditions:
      // 1. There should be at least 25% of face height worth of space below chin
      //    (relaxed from 40% - more lenient for shoulder visibility)
      const hasSpaceForShoulders = spaceBelowRatio >= 0.25;
      
      // 2. Face should not be in bottom portion of frame
      const faceNotTooLow = faceCenterRatio < 0.75;
      
      // 3. Face shouldn't be extremely large (taking up entire frame)
      const faceProperSize = faceHeightRatio >= 0.20 && faceHeightRatio <= 0.70;
      
      // 4. Minimal space at bottom (at least 12% of frame)
      const bottomSpace = (videoHeight - faceBottom) / videoHeight;
      const hasBottomSpace = bottomSpace >= 0.12;
      
      // Debug logging (enable for troubleshooting)
      // //console.log('Body Posture Check:', {
      //   spaceBelowRatio: spaceBelowRatio.toFixed(2),
      //   hasSpaceForShoulders,
      //   faceCenterRatio: faceCenterRatio.toFixed(2),
      //   faceNotTooLow,
      //   faceHeightRatio: faceHeightRatio.toFixed(2),
      //   faceProperSize,
      //   bottomSpace: bottomSpace.toFixed(2),
      //   hasBottomSpace,
      //   result: hasSpaceForShoulders && faceNotTooLow && faceProperSize && hasBottomSpace
      // });
      
      return hasSpaceForShoulders && faceNotTooLow && faceProperSize && hasBottomSpace;
    } catch (error) {
      console.error("Error in isProperBodyPosture:", error);
      return true; // Default to true to avoid false positives on errors
    }
  };

  const updateFaceTracker = (validFaces) => {
    const currentTime = performance.now();

    if (validFaces.length === 1) {
      const face = validFaces[0];
      const faceCenter = calculateFaceCenter(face.boundingBox);
      const faceArea = calculateFaceArea(face.boundingBox);

      const faceInfo = {
        center: faceCenter,
        area: faceArea,
        confidence: face.categories[0]?.score || 0,
        timestamp: currentTime,
        boundingBox: face.boundingBox,
      };

      faceTracker.current.faceHistory.push(faceInfo);
      if (
        faceTracker.current.faceHistory.length >
        faceTracker.current.maxHistorySize
      ) {
        faceTracker.current.faceHistory.shift();
      }

      faceTracker.current.primaryFace = faceInfo;
    }
  };

  const validateMultipleFaces = (faces) => {
    if (faces.length <= 1) return faces;

    const validatedFaces = [];
    const primaryFace = faceTracker.current.primaryFace;

    const sortedFaces = faces.sort((a, b) => {
      const areaA = calculateFaceArea(a.boundingBox);
      const areaB = calculateFaceArea(b.boundingBox);
      return areaB - areaA;
    });

    if (primaryFace && faceTracker.current.faceHistory.length >= 3) {
      for (const face of sortedFaces) {
        const faceCenter = calculateFaceCenter(face.boundingBox);
        const faceArea = calculateFaceArea(face.boundingBox);
        const confidence = face.categories[0]?.score || 0;

        const distance = Math.sqrt(
          Math.pow(faceCenter.x - primaryFace.center.x, 2) +
            Math.pow(faceCenter.y - primaryFace.center.y, 2)
        );

        const areaRatio =
          Math.min(faceArea, primaryFace.area) /
          Math.max(faceArea, primaryFace.area);

        const isLikelyPrimaryFace =
          distance < 80 && // Within 80 pixels of previous position
          areaRatio > 0.5 && // Similar size (50% overlap)
          confidence > 0.65; // Good confidence

        // This is likely a valid secondary face if:
        const isLikelySecondaryFace =
          distance > 100 && // Sufficiently far from primary face
          confidence > 0.75 && // High confidence for secondary face
          // Size validation for secondary faces:
          // Either a reasonably sized face (not tiny speck)
          ((faceArea > primaryFace.area * 0.15 &&
            faceArea < primaryFace.area * 1.5) ||
            // OR a smaller but confident distant face
            (faceArea > 400 && confidence > 0.85)); // At least 20x20 pixels with very high confidence

        if (isLikelyPrimaryFace || isLikelySecondaryFace) {
          validatedFaces.push(face);
        } else {
          // Log rejected detections for debugging
          // //console.log(`Rejected face detection:`, {
          //   distance: distance.toFixed(1),
          //   areaRatio: areaRatio.toFixed(3),
          //   confidence: confidence.toFixed(3),
          //   area: faceArea,
          //   reason:
          //     distance < 100
          //       ? "too close to primary"
          //       : confidence < 0.75
          //       ? "low confidence"
          //       : "size mismatch",
          // });
        }
      }
    } else {
      for (const face of sortedFaces) {
        const confidence = face.categories[0]?.score || 0;
        const faceArea = calculateFaceArea(face.boundingBox);

        if (confidence > 0.75 || (confidence > 0.65 && faceArea > 1000)) {
          validatedFaces.push(face);
        }
      }
    }

    return validatedFaces;
  };

  // Utility functions
  const distance = (point1, point2) => {
    if (!point1 || !point2 || point1.x === undefined || point1.y === undefined)
      return 0;
    return Math.sqrt(
      Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2)
    );
  };

  const midpoint = (point1, point2) => {
    if (!point1 || !point2) return { x: 0, y: 0 };
    return {
      x: (point1.x + point2.x) / 2,
      y: (point1.y + point2.y) / 2,
    };
  };

  const smoothGazeValue = (
    newValue,
    historyRef,
    smoothingFactor = configs.GAZE_SMOOTHING_FACTOR,
    maxHistorySize = 10
  ) => {
    if (isNaN(newValue)) return 0;

    historyRef.current.push(newValue);
    if (historyRef.current.length > maxHistorySize) {
      historyRef.current.shift();
    }
    const avg =
      historyRef.current.reduce((sum, val) => sum + val, 0) /
      historyRef.current.length;
    return smoothingFactor * newValue + (1 - smoothingFactor) * avg;
  };

  const calculateEyeAspectRatio = (eyeLandmarks) => {
    try {
      if (!eyeLandmarks || eyeLandmarks.length < 6) return 0.3;

      const verticalDist1 = distance(eyeLandmarks[1], eyeLandmarks[5]);
      const verticalDist2 = distance(eyeLandmarks[2], eyeLandmarks[4]);
      const horizontalDist = distance(eyeLandmarks[0], eyeLandmarks[3]);

      if (horizontalDist === 0) return 0.3;
      return (verticalDist1 + verticalDist2) / (2.0 * horizontalDist);
    } catch (error) {
      console.warn("Error calculating eye aspect ratio:", error);
      return 0.3;
    }
  };

  const calculateGazeDirection = (face) => {
  try {
    if (!face || face.length < 468) {
      console.warn("Invalid face landmarks for gaze calculation");
      return {
        gazeX: 0,
        gazeY: 0,
        headTurnAngle: 0,
        eyeCenterPoint: { x: 0, y: 0 },
        leftEyeCenter: { x: 0, y: 0 },
        rightEyeCenter: { x: 0, y: 0 },
      };
    }

    // Check if iris landmarks are available (MediaPipe provides them at indices 468-477)
    if (face.length < 478) {
      console.warn("Iris landmarks not available, using fallback eye center method");
      // Fallback to eye center method if iris data is missing
      const leftEye = {
        center: midpoint(face[33], face[133]),
        inner: face[133],
        outer: face[33],
      };

      const rightEye = {
        center: midpoint(face[362], face[263]),
        inner: face[362],
        outer: face[263],
      };

      const noseTip = face[1];
      const noseBridge = face[168];
      const leftEyeCenter = leftEye.center;
      const rightEyeCenter = rightEye.center;
      const eyeCenterMidpoint = midpoint(leftEyeCenter, rightEyeCenter);
      const faceWidth = distance(face[234], face[454]);
      const faceHeight = distance(face[10], face[152]);

      if (faceWidth === 0 || faceHeight === 0) {
        return {
          gazeX: 0,
          gazeY: 0,
          headTurnAngle: 0,
          eyeCenterPoint: eyeCenterMidpoint,
          leftEyeCenter,
          rightEyeCenter,
        };
      }

      const gazeX = ((eyeCenterMidpoint.x - noseTip.x) / faceWidth) * 2;
      const gazeY = ((eyeCenterMidpoint.y - noseBridge.y) / faceHeight) * 2;
      const faceCenter = midpoint(face[10], face[152]);
      const headTurnX = (eyeCenterMidpoint.x - faceCenter.x) / faceWidth;
      const headTurnAngle = headTurnX * 45;

      return {
        gazeX: smoothGazeValue(gazeX, gazeHistoryX),
        gazeY: smoothGazeValue(gazeY, gazeHistoryY),
        headTurnAngle,
        eyeCenterPoint: eyeCenterMidpoint,
        leftEyeCenter,
        rightEyeCenter,
      };
    }

    // Eye socket landmarks (the corners/bounds of the eye)
    const leftEyeOuter = face[33];   // Left eye outer corner
    const leftEyeInner = face[133];  // Left eye inner corner
    const rightEyeOuter = face[263]; // Right eye outer corner
    const rightEyeInner = face[362]; // Right eye inner corner

    const leftIrisCenter = face[468];   // Left iris center (pupil)
    const rightIrisCenter = face[473];  // Right iris center (pupil)

    // Calculate eye socket centers (these stay fixed relative to head)
    const leftEyeSocketCenter = midpoint(leftEyeOuter, leftEyeInner);
    const rightEyeSocketCenter = midpoint(rightEyeOuter, rightEyeInner);

    // Calculate eye socket dimensions
    const leftEyeWidth = distance(leftEyeOuter, leftEyeInner);
    const rightEyeWidth = distance(rightEyeOuter, rightEyeInner);

    if (leftEyeWidth === 0 || rightEyeWidth === 0) {
      return {
        gazeX: 0,
        gazeY: 0,
        headTurnAngle: 0,
        eyeCenterPoint: leftEyeSocketCenter,
        leftEyeCenter: leftEyeSocketCenter,
        rightEyeCenter: rightEyeSocketCenter,
      };
    }
    // This gives us the actual eyeball direction independent of head position  
    // Left eye gaze (iris relative to eye socket)
    const leftGazeX = (leftIrisCenter.x - leftEyeSocketCenter.x) / leftEyeWidth;
    const leftGazeY = (leftIrisCenter.y - leftEyeSocketCenter.y) / leftEyeWidth;

    // Right eye gaze (iris relative to eye socket)
    const rightGazeX = (rightIrisCenter.x - rightEyeSocketCenter.x) / rightEyeWidth;
    const rightGazeY = (rightIrisCenter.y - rightEyeSocketCenter.y) / rightEyeWidth;

    // Average both eyes for final gaze direction
    const gazeX = (leftGazeX + rightGazeX) / 2;
    const gazeY = (leftGazeY + rightGazeY) / 2;

    // Scale up for better sensitivity (adjust multiplier as needed)
    const scaledGazeX = gazeX * 3;
    const scaledGazeY = -gazeY * 2.5;

    // Head turn calculation (for reference, not used in gaze detection)
    const faceWidth = distance(face[234], face[454]);
    const faceCenter = midpoint(face[10], face[152]);
    const eyeCenterMidpoint = midpoint(leftEyeSocketCenter, rightEyeSocketCenter);
    const headTurnX = (eyeCenterMidpoint.x - faceCenter.x) / faceWidth;
    const headTurnAngle = headTurnX * 45;

    return {
      gazeX: smoothGazeValue(scaledGazeX, gazeHistoryX),
      gazeY: smoothGazeValue(scaledGazeY, gazeHistoryY),
      headTurnAngle,
      eyeCenterPoint: eyeCenterMidpoint,
      leftEyeCenter: leftEyeSocketCenter,
      rightEyeCenter: rightEyeSocketCenter,
    };
  } catch (error) {
    console.error("Error in calculateGazeDirection:", error);
    return {
      gazeX: 0,
      gazeY: 0,
      headTurnAngle: 0,
      eyeCenterPoint: { x: 0, y: 0 },
      leftEyeCenter: { x: 0, y: 0 },
      rightEyeCenter: { x: 0, y: 0 },
    };
  }
};

  const quantizeGazeDirection = (gazeX, gazeY) => {
    const threshold = configs.GAZE_X_THRESHOLD || 0.3;
    const thresholdY = configs.GAZE_Y_THRESHOLD || 0.5;
    
    const absX = Math.abs(gazeX);
    const absY = Math.abs(gazeY);

    // //console.log('Gaze Values:', {
    //   gazeX: gazeX.toFixed(2),
    //   gazeY: gazeY.toFixed(2),
    //   absX: absX.toFixed(2),
    //   absY: absY.toFixed(2),
    //   threshold: threshold,
    //   withinThreshold: absX < threshold && absY < thresholdY
    // });
  
    
    if (absX < threshold && absY < thresholdY) {
      return 'CENTER';
    }
    
    let direction = '';
    
    if (absY >= thresholdY) {
      if (gazeY > thresholdY) {
        direction += 'UP';
      } else if (gazeY < -thresholdY) {
        direction += 'DOWN';
      }
    }
    
    if (absX >= threshold) {
      if (direction) direction += '_';
      
      if (gazeX > threshold) {
        direction += 'LEFT';
      } else if (gazeX < -threshold) {
        direction += 'RIGHT';
      }
    }
    
    return direction || 'CENTER';
  };

  const addGazeToHistory = (direction, timestamp) => {
    const WINDOW_SIZE_MS = 10000;
    
    gazeDirectionHistory.current.push({
      direction,
      timestamp,
    });
    
    const cutoffTime = timestamp - 30000;
    gazeDirectionHistory.current = gazeDirectionHistory.current.filter(
      entry => entry.timestamp > cutoffTime
    );
    
    const windowStart = timestamp - WINDOW_SIZE_MS;
    gazeWindow.current = gazeDirectionHistory.current.filter(
      entry => entry.timestamp > windowStart
    );
  };

  const calculateDirectionalDominance = () => {
    if (gazeWindow.current.length === 0) {
      return { dominantDirection: null, dominanceScore: 0, distribution: {} };
    }
    
    const directionCounts = {};
    let totalOffScreenLooks = 0;
    
    gazeWindow.current.forEach(entry => {
      if (entry.direction !== 'CENTER') {
        directionCounts[entry.direction] = (directionCounts[entry.direction] || 0) + 1;
        totalOffScreenLooks++;
      }
    });
    
    if (totalOffScreenLooks === 0) {
      return { dominantDirection: null, dominanceScore: 0, distribution: directionCounts };
    }
    
    let dominantDirection = null;
    let maxCount = 0;
    
    Object.entries(directionCounts).forEach(([direction, count]) => {
      if (count > maxCount) {
        maxCount = count;
        dominantDirection = direction;
      }
    });
    
    const dominanceScore = maxCount / totalOffScreenLooks;
    
    return {
      dominantDirection,
      dominanceScore,
      distribution: directionCounts,
      totalOffScreenLooks,
      maxCount,
    };
  };

  //Step 5 - Temporal consistency check
  const checkTemporalConsistency = (dominantDirection) => {
    const CONSISTENCY_THRESHOLD = 3;
    
    if (!dominantDirection) {
      dominantDirectionHistory.current = [];
      return false;
    }
    
    dominantDirectionHistory.current.push(dominantDirection);
    
    if (dominantDirectionHistory.current.length > 5) {
      dominantDirectionHistory.current.shift();
    }
    
    if (dominantDirectionHistory.current.length < CONSISTENCY_THRESHOLD) {
      return false;
    }
    
    const lastThree = dominantDirectionHistory.current.slice(-CONSISTENCY_THRESHOLD);
    const allSame = lastThree.every(dir => dir === dominantDirection);
    
    return allSame;
  };

  const detectGazeViolation = (gazeX, gazeY, timestamp, eyesOpen) => {
    
    if (!eyesOpen) {
      return { violation: false, message: null };
    }
    
    const GRACE_PERIOD_MS = 500;
    const direction = quantizeGazeDirection(gazeX, gazeY);

    if (direction === 'CENTER') {
      gazeAwayStartTime.current = null;
      lastGazeDirection.current = null;

      const cutoffTime = timestamp - 30000;
      gazeDirectionHistory.current = gazeDirectionHistory.current.filter(
        entry => entry.timestamp > cutoffTime
      );
      const windowStart = timestamp - 10000;
      gazeWindow.current = gazeDirectionHistory.current.filter(
        entry => entry.timestamp > windowStart
      );
    } else {
      if (gazeAwayStartTime.current === null || lastGazeDirection.current !== direction) {
        gazeAwayStartTime.current = timestamp;
        lastGazeDirection.current = direction;
      }

      if (timestamp - gazeAwayStartTime.current >= GRACE_PERIOD_MS) {
        addGazeToHistory(direction, timestamp);
      }
    }

    if (gazeWindow.current.length < 15) {
      return { violation: false, message: null };
    }
    
    const dominanceData = calculateDirectionalDominance();
    const isConsistent = checkTemporalConsistency(dominanceData.dominantDirection);

    // //console.log('Gaze Analysis:', {
    //   currentDirection: direction,
    //   windowSize: gazeWindow.current.length,
    //   dominantDirection: dominanceData.dominantDirection,
    //   dominanceScore: dominanceData.dominanceScore?.toFixed(2),
    //   distribution: dominanceData.distribution,
    //   isTemporallyConsistent: isConsistent,
    //   totalOffScreenLooks: dominanceData.totalOffScreenLooks
    // });
      
    const DOMINANCE_THRESHOLD = 0.7;
    const MIN_OFF_SCREEN_LOOKS = 8;
    
    const isViolation = 
      dominanceData.dominantDirection && 
      dominanceData.dominanceScore >= DOMINANCE_THRESHOLD &&
      dominanceData.totalOffScreenLooks >= MIN_OFF_SCREEN_LOOKS &&
      isConsistent;
    
    const VIOLATION_COOLDOWN_MS = 5000;
    if (isViolation && (timestamp - lastGazeViolationTime.current) < VIOLATION_COOLDOWN_MS) {
      return { violation: false, message: null };
    }
    
    if (isViolation) {
      lastGazeViolationTime.current = timestamp;
      
      const directionText = dominanceData.dominantDirection
        .replace(/_/g, ' and ')
        .toLowerCase();

       //console.log('🚨 GAZE VIOLATION DETECTED:', {
      //   direction: direction,
      //   windowSize: gazeWindow.current.length,
      //   dominantDirection: dominanceData.dominantDirection,
      //   dominanceScore: dominanceData.dominanceScore.toFixed(2),
      //   distribution: dominanceData.distribution,
      //   totalOffScreenLooks: dominanceData.totalOffScreenLooks,
      //   message: `Suspicious gaze pattern detected. Repeatedly looking ${directionText}.`
      // });
      
      return {
        violation: true,
        message: `Suspicious gaze pattern detected. Repeatedly looking ${directionText}.`,
        details: {
          dominantDirection: dominanceData.dominantDirection,
          dominanceScore: dominanceData.dominanceScore.toFixed(2),
          offScreenLooks: dominanceData.totalOffScreenLooks,
          distribution: dominanceData.distribution,
        }
      };
    }
    
    return { violation: false, message: null };
  };

  const detectEyeState = (face) => {
    try {
      if (!face || face.length < 468) {
        return {
          eyeAspectRatio: 0.3,
          eyesOpen: true,
          leftEAR: 0.3,
          rightEAR: 0.3,
        };
      }

      const leftEyeLandmarks = [
        face[33],
        face[160],
        face[158],
        face[133],
        face[153],
        face[144],
      ];
      const rightEyeLandmarks = [
        face[362],
        face[385],
        face[387],
        face[263],
        face[373],
        face[380],
      ];

      const leftEAR = calculateEyeAspectRatio(leftEyeLandmarks);
      const rightEAR = calculateEyeAspectRatio(rightEyeLandmarks);
      const avgEAR = (leftEAR + rightEAR) / 2;

      const smoothedEAR = smoothGazeValue(avgEAR, eyeAspectRatioHistory);

      return {
        eyeAspectRatio: smoothedEAR,
        eyesOpen: smoothedEAR > configs.EYE_ASPECT_RATIO_THRESHOLD,
        leftEAR,
        rightEAR,
      };
    } catch (error) {
      console.error("Error in detectEyeState:", error);
      return {
        eyeAspectRatio: 0.3,
        eyesOpen: true,
        leftEAR: 0.3,
        rightEAR: 0.3,
      };
    }
  };

  // Toast management
  const showViolationToast = (violation_type, message) => {
    if (!activeToastsRef.current[violation_type]) {
      const id = toast.error(message, {
        toastId: violation_type,
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
      });
      activeToastsRef.current[violation_type] = id;
      setActiveViolationToasts((prev) => ({
        ...prev,
        [violation_type]: id,
      }));
    }
  };

  const dismissViolationToast = (violation_type) => {
    const id = activeToastsRef.current[violation_type];
    if (id) {
      toast.dismiss(id);
      delete activeToastsRef.current[violation_type];
      setActiveViolationToasts((prev) => {
        const newObj = { ...prev };
        delete newObj[violation_type];
        return newObj;
      });
    }
  };

  // Fullscreen management
  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Screenshot capture
  const captureScreenshot = () => {
    if (!videoRef.current) return null;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
    });
  };

  // Violation logging
  const logViolationWithCooldown = async (violation_type, meta = {}) => {
    try {
      let cooldownKey = violation_type;
      if (violation_type === "prohibited_item" && meta.device_name) {
        cooldownKey = `prohibited_item_${meta.device_name}`;
      }
      const now = Date.now();
      const lastLoggedTime = lastLoggedMap.current[cooldownKey];

      if (
        !lastLoggedTime ||
        now - lastLoggedTime >= configs.VIOLATION_COOLDOWN_MS
      ) {
        lastLoggedMap.current[cooldownKey] = now;

        const screenshotBlob = await captureScreenshot();
        if (!screenshotBlob) {
          console.warn("No screenshot available for violation");
          return logViolationToBackend(violation_type, meta);
        }

        const formData = new FormData();
        formData.append("screenshot", screenshotBlob, "screenshot.jpg");
        formData.append("violation_type", violation_type);
        if (meta.device_name) {
          formData.append("device_name", meta.device_name);
        }
        if (meta.confidence_percent) {
          formData.append(
            "confidence_percent",
            meta.confidence_percent.toString()
          );
        }

        // Gaze-specific metadata
        if (violation_type === "gaze") {
          if (meta.gaze_direction) {
            formData.append("gaze_direction", meta.gaze_direction);
          }
        }

        await axios.post(`${host}/log_violation/${interviewId}`, formData, {
          headers: { "Content-Type": "multipart/form-data", ...candidateAuthHeader(interviewId) },
        });

        onViolation(violation_type, meta);
      } 
    } catch (error) {
      console.error("Failed to log violation with screenshot:", error);
    }
  };

  const logViolationToBackend = async (violation_type, meta = {}) => {
    try {
      const formData = new FormData();
      formData.append("violation_type", violation_type);
      formData.append("timestamp", new Date().toISOString());
      if (violation_type === "prohibited_item") {
        if (meta.device_name) formData.append("device_name", meta.device_name);
        if (meta.confidence_percent)
          formData.append(
            "confidence_percent",
            meta.confidence_percent.toString()
          );
      }

      if(violation_type === "tab_switch" && meta.img){
        formData.append("screenshot", meta.img, "tab_switch_screenshot.png");
      }
      await axios.post(`${host}/log_violation/${interviewId}`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...candidateAuthHeader(interviewId) },
      });
      console.info(`Logged violation: ${violation_type}`);
      onViolation(violation_type, meta);
    } catch (error) {
      console.error(`Failed to log violation: ${violation_type}`, error);
    }
  };

  const extractViolationType = (alertMessage) => {
    const msg = alertMessage.toLowerCase();
    if (msg.includes("face not detected")) return "no_face";
    if (msg.includes("multiple faces")) return "multiple_faces";
    if (msg.includes("prohibited item")) return "prohibited_item";
    if (msg.includes("head turned") || msg.includes("head turn"))
      return "head_turn";
    if (
      msg.includes("looking away") ||
      msg.includes("gaze") ||
      msg.includes("eye focus") ||
      msg.includes("eyes appear to be closed") ||
      msg.includes("suspicious gaze pattern")
    )
      return "gaze";
    return null;
  };

  // Enhanced camera management with permission checks and retry logic
  const startCamera = async (retryCount = 0) => {
    try {
      // Step 1: Check if we already have an active stream to prevent unnecessary permissions
      if (streamRef.current && streamRef.current.active) {
        //console.log("📹 Using existing active camera stream");

        // Ensure video element is properly connected
        if (
          videoRef.current &&
          videoRef.current.srcObject !== streamRef.current
        ) {
          videoRef.current.srcObject = streamRef.current;
          try {
            await videoRef.current.play();
          } catch (playError) {
            console.warn("Video play interrupted, retrying...", playError);
            // Small delay before retry
            await new Promise((resolve) => setTimeout(resolve, 100));
            await videoRef.current.play();
          }
        }

        setIsCameraActive(true);
        onCameraReady(true);
        return;
      }

      // Step 2: Check for stream handoff from InterviewInstructions
      const handoffInfo = location.state;
      //console.log("Checking for stream handoff:", handoffInfo);

      if (handoffInfo?.streamHandoff && handoffInfo?.permissionsGranted) {
        const timeSinceHandoff = Date.now() - handoffInfo.timestamp;

        // If handoff is recent (within 5 seconds), we can likely reuse permissions
        if (timeSinceHandoff < 5000) {
          // console.log(
          //   "Recent handoff detected, attempting quick camera access..."
          // );
          // Skip permission checks and try direct access
        }
      }

      // Step 3: Check browser permission status (if supported)
      if (navigator.permissions) {
        try {
          const cameraPermission = await navigator.permissions.query({
            name: "camera",
          });

          //console.log(" Permission status:", {
          //   camera: cameraPermission.state,
          //   handoffInfo: handoffInfo ? "present" : "none",
          // });

          if (cameraPermission.state === "denied") {
            console.warn("⚠️ Camera permission denied by user");
            toast.error(
              "Camera access denied. Please enable permissions in browser settings."
            );
            setIsCameraActive(false);
            onCameraReady(false);
            return;
          }
        } catch (permError) {
          console.warn("Permission query not supported or failed:", permError);
          // Continue with getUserMedia attempt
        }
      }

      // Step 3: Request new media stream (VIDEO ONLY - no audio needed for proctoring)
      // Audio is handled separately by InterviewPortal for answer recording
      //console.log(`📷 Requesting camera access (attempt ${retryCount + 1})...`);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      // Step 4: Setup stream and video element
      streamRef.current = stream;
      if (videoRef.current) {
        // Clear any existing srcObject first to prevent conflicts
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
        }

        videoRef.current.srcObject = stream;

        try {
          await videoRef.current.play();
          //console.log("✅ Video playback started successfully");
        } catch (playError) {
          // Handle play interruption gracefully with exponential backoff
          console.warn(
            `🔄 Video play interrupted (attempt ${
              retryCount + 1
            }), retrying...`,
            playError
          );
          const delay = Math.min(100 * Math.pow(2, retryCount), 1000); // 100ms, 200ms, 400ms, max 1s
          await new Promise((resolve) => setTimeout(resolve, delay));

          try {
            await videoRef.current.play();
            //console.log("✅ Video playback succeeded after retry");
          } catch (retryError) {
            console.error("❌ Failed to play video after retry:", retryError);
            throw retryError;
          }
        }

        // Step 5: Wait for video metadata and setup canvas
        await new Promise((resolve) => {
          if (videoRef.current.readyState >= 2) return resolve();
          videoRef.current.onloadedmetadata = resolve;
        });

        if (canvasRef.current) {
          canvasRef.current.width = videoRef.current.videoWidth || 640;
          canvasRef.current.height = videoRef.current.videoHeight || 480;
        }
      }

      // Step 6: Update component state
      setIsCameraActive(true);
      onCameraReady(true);

      //console.log("🎉 Camera setup completed successfully (video only, no audio)");
    } catch (error) {
      console.error("Camera setup error:", {
        name: error.name,
        message: error.message,
        retryCount,
        timestamp: new Date().toISOString(),
      });

      // Step 8: Intelligent retry logic based on error type
      if (error.name === "AbortError" && retryCount < 2) {
        const retryDelay = 500 + retryCount * 500; // 500ms, 1s, 1.5s
        setTimeout(() => startCamera(retryCount + 1), retryDelay);
        return;
      }

      if (error.name === "NotAllowedError") {
        toast.error(
          "Camera access denied. Please enable camera permissions and refresh the page."
        );
      } else if (error.name === "NotFoundError") {
        toast.error("No camera found. Please connect a camera and try again.");
      } else if (error.name === "NotReadableError") {
        toast.error(
          "Camera is being used by another application. Please close other apps and try again."
        );
      } else {
        toast.error(
          "Couldn't access camera or microphone. Please check permissions and try again."
        );
      }

      setIsCameraActive(false);
      onCameraReady(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    onCameraReady(false);
  };

  // NEW: Use pre-initialized MediaPipe detectors from context
  const initializeMediaPipe = async () => {
    if (!isEnabled) return;

    if (!configs) {
      return; // Exit early, useEffect will retry when configs become available
    }

    if (isMediaPipeReady) {
      const detectors = getDetectors();
      if (
        detectors.faceDetector &&
        detectors.handLandmarker &&
        detectors.objectDetector &&
        detectors.faceLandmarker
      ) {
        setFaceDetector(detectors.faceDetector);
        setHandLandmarker(detectors.handLandmarker);
        setObjectDetector(detectors.objectDetector);
        setFaceLandmarker(detectors.faceLandmarker);
        //console.log(
        //   "Using pre-loaded MediaPipe detectors - instant proctoring start!"
        // );
        return;
      }
    }

    // Fallback initialization if context is not ready
    //console.log(
    //   "MediaPipe context not ready, falling back to manual initialization"
    // );
    try {
      const {
        FaceDetector,
        HandLandmarker,
        ObjectDetector,
        FaceLandmarker,
        FilesetResolver,
      } = await import("@mediapipe/tasks-vision");

      const vision = await FilesetResolver.forVisionTasks(
        MODEL_URLS.WASM_BASE
      );

      const [faceDetector, handLandmarker, objectDetector, faceLandmarker] =
        await Promise.all([
          FaceDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                MODEL_URLS.FACE_DETECTOR,
              delegate: "GPU",
            },
            runningMode: "VIDEO",
            minDetectionConfidence: configs.MIN_FACE_DETECTION_CONFIDENCE,
            inputStreamHandler: "ImmediateInputStreamHandler",
          }),
          HandLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                MODEL_URLS.HAND_LANDMARKER,
              delegate: "GPU",
            },
            runningMode: "VIDEO",
            numHands: configs.NUM_HANDS,
            inputStreamHandler: "ImmediateInputStreamHandler",
          }),
          ObjectDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                MODEL_URLS.OBJECT_DETECTOR,
              delegate: "GPU",
            },
            scoreThreshold: configs.SCORE_THRESHOLD,
            runningMode: "VIDEO",
            inputStreamHandler: "ImmediateInputStreamHandler",
          }),
          FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                MODEL_URLS.FACE_LANDMARKER,
              delegate: "GPU",
            },
            runningMode: "VIDEO",
            numFaces: configs.NUM_FACES,
          }),
        ]);

      setFaceDetector(faceDetector);
      setHandLandmarker(handLandmarker);
      setObjectDetector(objectDetector);
      setFaceLandmarker(faceLandmarker);
      //console.log("MediaPipe detectors initialized manually");
    } catch (error) {
      console.error("Error initializing MediaPipe:", error);
      toast.error("Failed to initialize proctoring system.");
    }
  };

  const detectProhibitedObjects = (objectResults) => {
    const prohibitedItems = ["cell phone", "laptop", "smartwatch", "tv"];

    const detectedProhibited = [];

    objectResults.detections.forEach((detection) => {
      const categories = detection.categories;

      categories.forEach((category) => {
        const categoryName = category.categoryName.toLowerCase();
        const confidence = category.score;

        const isProhibited = prohibitedItems.some(
          (item) => categoryName.includes(item) || item.includes(categoryName)
        );

        if (isProhibited && confidence > configs.SCORE_THRESHOLD) {
          detectedProhibited.push({
            name: categoryName,
            confidence: confidence,
            boundingBox: {
              x: detection.boundingBox.originX,
              y: detection.boundingBox.originY,
              width: detection.boundingBox.width,
              height: detection.boundingBox.height,
            },
          });
        }
      });
    });

    return detectedProhibited;
  };

  function isOverlap(boxA, boxB, threshold = 0.4) {
    // Returns true if overlap area / boxA area > threshold
    const xOverlap = Math.max(
      0,
      Math.min(boxA.x + boxA.width, boxB.x + boxB.width) -
        Math.max(boxA.x, boxB.x)
    );
    const yOverlap = Math.max(
      0,
      Math.min(boxA.y + boxA.height, boxB.y + boxB.height) -
        Math.max(boxA.y, boxB.y)
    );
    const overlapArea = xOverlap * yOverlap;
    const areaA = boxA.width * boxA.height;
    return areaA > 0 && overlapArea / areaA > threshold;
  }

  const isProctoringReady = () => {
    return (
      configs && // Configs must be loaded
      faceDetector &&
      handLandmarker &&
      objectDetector &&
      faceLandmarker // All detectors ready
    );
  };

  // Main proctoring logic
  const runProctoring = async () => {
    if (
      !isEnabled ||
      !isInterviewActive ||
      !isProctoringReady() ||
      !videoRef.current ||
      !canvasRef.current
    )
      return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video.videoWidth || !video.videoHeight) return;
    const ctx = canvas.getContext("2d");
    const now = performance.now();

    // Skip throttling for first detection to get immediate alerts
    if (
      lastDetectionTime.current !== 0 &&
      now - lastDetectionTime.current < 100
    )
      return;
    lastDetectionTime.current = now;

    const alerts = [];
    const violationTypes = new Set();

    // Face detection
    const faceResults = await faceDetector.detectForVideo(video, now);
    const confidentDetections = faceResults.detections.filter(
      (detection) =>
        detection.categories[0]?.score >= configs.MIN_FACE_DETECTION_CONFIDENCE
    );

    const handResults = await handLandmarker.detectForVideo(video, now);

    // Generate bounding boxes for each detected hand
    const handBoxes = handResults.landmarks.map((landmarkSet) => {
      const xs = landmarkSet.map((pt) => pt.x);
      const ys = landmarkSet.map((pt) => pt.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return {
        x: minX * video.videoWidth,
        y: minY * video.videoHeight,
        width: (maxX - minX) * video.videoWidth,
        height: (maxY - minY) * video.videoHeight,
      };
    });

    // Filter out faces that overlap with hands
    const filteredFaces = confidentDetections.filter((faceDet) => {
      const faceBox = faceDet.boundingBox
        ? {
            x: faceDet.boundingBox.originX,
            y: faceDet.boundingBox.originY,
            width: faceDet.boundingBox.width,
            height: faceDet.boundingBox.height,
          }
        : null;
      if (!faceBox) return true; // keep if bounding box missing

      return !handBoxes.some((handBox) => isOverlap(faceBox, handBox, 0.4));
    });

    // NEW: Enhanced face validation with improved accuracy
    const validFaceDetections = filteredFaces.filter(isValidFaceDetection);
    const finalValidatedFaces = validateMultipleFaces(validFaceDetections);
    const finalFaceCount = finalValidatedFaces.length;

    // Update face tracking
    updateFaceTracker(finalValidatedFaces);

    // Enhanced multiple face detection with temporal validation
    if (finalFaceCount > 1) {
      consecutiveMultiFaceFrames.current++;

      // Only trigger alert after multiple consecutive detections
      const requiredConsecutiveFrames = 8; // About 1.6 seconds at 200ms intervals

      if (consecutiveMultiFaceFrames.current >= requiredConsecutiveFrames) {
        if (
          !lastMultiFaceTime.current ||
          now - lastMultiFaceTime.current > 5000
        ) {
          // 5 second cooldown

          lastMultiFaceTime.current = now;

          // Enhanced logging with face details
          const faceDetails = finalValidatedFaces.map((face, index) => ({
            id: index + 1,
            confidence: (face.categories[0]?.score || 0).toFixed(3),
            area: calculateFaceArea(face.boundingBox),
            center: calculateFaceCenter(face.boundingBox),
          }));

          // //console.log(`Multiple faces validated:`, faceDetails);

          const msg = `Multiple faces detected. Please ensure you are alone while taking the interview.`;
          alerts.push(msg);
          const type = extractViolationType(msg);
          if (type) {
            violationTypes.add(type);
            onMultipleFaceViolation();
            // //console.log(`Proctoring Alert: (${type}): ${msg}`);
            logViolationWithCooldown(type, {
              face_count: finalFaceCount,
              detection_confidence: finalValidatedFaces.map(
                (f) => f.categories[0]?.score || 0
              ),
              consecutive_frames: consecutiveMultiFaceFrames.current,
            });
          }
        }
      }
    } else {
      consecutiveMultiFaceFrames.current = 0;
      lastMultiFaceTime.current = null;

      // Dismiss any active multiple face toast
      if (activeToastsRef.current["multiple_faces"]) {
        dismissViolationToast("multiple_faces");
      }
    }

    // No face detection (updated to use finalFaceCount)
    if (finalFaceCount === 0) {
      if (noFaceSinceRef.current === null) {
        noFaceSinceRef.current = now;
      }

      if (now - noFaceSinceRef.current > configs.NO_FACE_THRESHOLD_MS) {
        const msg =
          "Face not detected. Please ensure your face is clearly visible to the camera.";
        alerts.push(msg);
        const type = extractViolationType(msg);
        if (type) {
          violationTypes.add(type);
          onFaceViolation();
          logViolationWithCooldown(type);
        }
      }

      // Object detection when no face is present
      const objectResults = await objectDetector.detectForVideo(video, now);
      const prohibitedObjects = detectProhibitedObjects(objectResults);
      prohibitedObjects.forEach((obj) => {
        const alertMsg =
          "Prohibited item detected. Please remove any phones, or objects from the view.";
        alerts.push(alertMsg);
        const objType = extractViolationType(alertMsg) || "prohibited_item";
        violationTypes.add(objType);
        onProhibitedObjectViolation();
        // //console.log(
        //   `Proctoring Alert (console/back): (${objType}): ${alertMsg}`
        // );
        logViolationWithCooldown("prohibited_item", {
          device_name: obj.name,
          confidence_percent: Math.round(obj.confidence * 100),
        });
      });

      // Dismiss any non-relevant violation toasts
      Object.keys(activeToastsRef.current).forEach((type) => {
        if (!violationTypes.has(type)) {
          dismissViolationToast(type);
        }
      });

      setProctoringAlerts(alerts);
      return;
    } else {
      // Face is present - reset no face tracking
      noFaceSinceRef.current = null;
      if (activeToastsRef.current["no_face"]) {
        dismissViolationToast("no_face");
      }

      const objectResults = await objectDetector.detectForVideo(video, now);
      const prohibitedObjects = detectProhibitedObjects(objectResults);
      prohibitedObjects.forEach((obj) => {
        const alertMsg = `Prohibited item detected: ${obj.name} (${Math.round(
          obj.confidence * 100
        )}% confidence)`;
        alerts.push(alertMsg);
        const objType = extractViolationType(alertMsg) || "prohibited_item";
        violationTypes.add(objType);
        onProhibitedObjectViolation();
        logViolationWithCooldown("prohibited_item", {
          device_name: obj.name,
          confidence_percent: Math.round(obj.confidence * 100),
        });
      });

      Object.keys(activeToastsRef.current).forEach((type) => {
        if (!violationTypes.has(type)) {
          dismissViolationToast(type);
        }
      });
    }

    if (finalFaceCount === 1 && faceLandmarker) {
      const faceLandmarksResult = await faceLandmarker.detectForVideo(
        video,
        now
      );
      const face = faceLandmarksResult.faceLandmarks?.[0];
      if (face && face.length >= 468) {
        const hasProperPosture = isProperBodyPosture(
          face,
          video.videoWidth,
          video.videoHeight
        );

        if (!hasProperPosture) {
          if (bodyMissingSinceRef.current === null) {
            bodyMissingSinceRef.current = now;
            //console.log("Started tracking body posture absence at:", now);
          } else if (now - bodyMissingSinceRef.current >= 10000) {
            //console.log("Body posture violation detected after 10 seconds");
            onBodyPostureViolation();
            logViolationWithCooldown("body_not_visible");
            bodyMissingSinceRef.current = now;
          }
        } else {
          bodyMissingSinceRef.current = null;
        }
        
        const gazeData = calculateGazeDirection(face);
        const eyeState = detectEyeState(face);

        const gazeViolationResult = detectGazeViolation(
          gazeData.gazeX,
          gazeData.gazeY,
          now,
          eyeState.eyesOpen
        );

        if (gazeViolationResult.violation) {
          const msg = gazeViolationResult.message;
          
          if (!violationTypes.has("gaze")) {
            onGazeViolation();
            logViolationWithCooldown("gaze", {
              gaze_direction: gazeViolationResult.details?.dominantDirection || "UNKNOWN",
            });
          }
          violationTypes.add("gaze");
          alerts.push(msg);
        } else {
          if (activeToastsRef.current["gaze"]) {
            dismissViolationToast("gaze");
          }
        }

        // Eye closure detection
        if (!eyeState.eyesOpen) {
          if (!eyeClosedSince.current) eyeClosedSince.current = now;
          if (
            now - eyeClosedSince.current >
            configs.ALLOWED_LOOKING_DOWN_DURATION
          ) {
            const msg =
              "Eyes appear to be closed for an extended period. Please keep your eyes open and focused on the screen.";
            if (!violationTypes.has("gaze")) {
              onGazeViolation();
              logViolationWithCooldown("gaze");
            }
            violationTypes.add("gaze");
            alerts.push(msg);
          }
        } else {
          eyeClosedSince.current = null;
          if (activeToastsRef.current["gaze"]) {
            dismissViolationToast("gaze");
          }
        }
      } else {
        console.warn("No valid face landmarks detected");
        bodyMissingSinceRef.current = null;
      }
    } else {
      bodyMissingSinceRef.current = null;
    }
    setProctoringAlerts(alerts);
  };

  // Effects
  useEffect(() => {
    const handleFullscreenChange = async () => {
      const fullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(fullscreen);
      onFullscreenChange(fullscreen);

      // Only enforce fullscreen when both proctoring is enabled AND interview is active
      if (!fullscreen && isInterviewActive) {
        if (onFullscreenExitViolation) {
          // const screenShot = await captureScreenshotBlob();
          onFullscreenExitViolation();
        }
        logViolationToBackend("fullscreen_exit");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [isInterviewActive, onFullscreenExitViolation]);
   
  useEffect(() => {
    if (!isFullscreen && isInterviewActive) {
      setTimeout(() => {
      }, 100);
    }
  }, [isFullscreen, isInterviewActive]);

  useEffect(() => {
    const handleVisibilityChange =async () => {
      if (document.hidden && isInterviewActive) {
        let screenShot = null;
        if (onTabSwitchViolation) {
          screenShot = await captureScreenshotBlob();
          onTabSwitchViolation();
        }
        logViolationToBackend("tab_switch",{
          img:screenShot
        });
      }
    };

    if (isInterviewActive) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isInterviewActive, onTabSwitchViolation]); 

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isInterviewActive) return; 

      if (
        (e.ctrlKey || e.metaKey) &&
        ["c", "v", "x", "C", "V", "X"].includes(e.key)
      ) {
        e.preventDefault();
        toast.warn(
          "Restricted action detected. Copying or pasting is not allowed during the interview."
        );
        logViolationToBackend("copy_paste_actions");
        return;
      }
      if (
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i") ||
        e.key === "F12"
      ) {
        e.preventDefault();
        logViolationToBackend("devtools_open");
        return;
      }

      if (e.altKey && e.key === "Tab") {
        e.preventDefault();
        logViolationToBackend("alt_tab_detected");
        return;
      }

      // Additional Alt key combinations that could be used for task switching
      if (e.altKey && (e.key === "Escape" || e.key === "F4")) {
        e.preventDefault();
        logViolationToBackend("window_switch_attempt");
        return;
      }

      // Windows key restrictions (Start menu, Windows shortcuts)
      if (e.key === "Meta" || e.metaKey) {
        if (navigator.platform.toLowerCase().includes("win")) {
          e.preventDefault();
          logViolationToBackend("windows_key_detected");
          return;
        }
      }

      // Ctrl+Alt+Delete, Ctrl+Shift+Esc (Task Manager)
      if (
        (e.ctrlKey && e.altKey && e.key === "Delete") ||
        (e.ctrlKey && e.shiftKey && e.key === "Escape")
      ) {
        e.preventDefault();
        logViolationToBackend("system_tools_attempt");
        return;
      }
    };

    const handlePaste = (e) => {
      if (!isInterviewActive) return; 
      e.preventDefault();
      logViolationToBackend("paste_action_detected");
      return;
    };

    const handleCopyCut = (e) => {
      if (!isInterviewActive) return; 
      e.preventDefault();
      logViolationToBackend("copy_action_detected");
      return;
    };

    if (isInterviewActive) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("paste", handlePaste);
      document.addEventListener("copy", handleCopyCut);
      document.addEventListener("cut", handleCopyCut);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("copy", handleCopyCut);
      document.removeEventListener("cut", handleCopyCut);
    };
  }, [isInterviewActive]); 

  useEffect(() => {
    if (isMultiMonitor && isInterviewActive) {
      if (isMultiMonitor && isInterviewActive) {
        onStickyAlert("multi_monitor");
        logViolationToBackend("multi_monitor_detected");
        return;
      }
    }
  }, [isMultiMonitor, isInterviewActive]); 
  useEffect(() => {
    if (isEnabled) {
      initializeMediaPipe();
    }
  }, [isEnabled, isMediaPipeReady, configs]);

  useEffect(() => {
    if (!isCameraActive && !streamRef.current?.active) {
      //console.log("🚀 Starting camera due to isEnabled change");
      startCamera();
    }
  }, [isEnabled]);

  useEffect(() => {
    let interval;
    if (
      isEnabled &&
      isInterviewActive &&
      isCameraActive &&
      videoRef.current &&
      canvasRef.current &&
      faceDetector &&
      handLandmarker &&
      objectDetector &&
      faceLandmarker
    ) {
      interval = setInterval(runProctoring, 200);
    }
    return () => clearInterval(interval);
  }, [
    isEnabled,
    isInterviewActive,
    isCameraActive,
    faceDetector,
    handLandmarker,
    objectDetector,
    faceLandmarker,
  ]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className={`${className} relative w-full h-full`}>
      <video
        ref={videoRef}
        autoPlay
        muted
        className="w-full h-full object-cover rounded-2xl"
      />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full rounded-2xl"
        style={{ display: isCameraActive ? "block" : "none" }}
      />
    </div>
  );
};

export default ProctoringSystem;